import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import {parseBands} from 'molbio/virtual-digests/util';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';

export interface LadderBandsEditorProps {
  bandSizes: Array<number>;
  isReadOnly: boolean;
  onSave: (parsedBands: Array<number>) => Promise<void>;
}

export default function LadderBandsEditor({
  bandSizes,
  isReadOnly,
  onSave,
}: LadderBandsEditorProps): JSX.Element {
  const [textValue, setTextValue] = useState(bandSizes.join('\n'));
  const [parsedBands, setParsedBands] = useState<Array<number>>(bandSizes);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setTextValue(newText);
    const parsed = parseBands(newText);
    setParsedBands(parsed);
  }, []);

  return (
    <div>
      {isReadOnly ? (
        <p className={css(styles.instructions)}>You do not have permission to edit this ladder.</p>
      ) : (
        <p className={css(styles.instructions)}>Enter the size of each band (in bp), one per line.</p>
      )}
      <div className="row">
        <div className="col-md-6">
          <textarea
            aria-label="Band sizes"
            className={classNames(css(styles.textarea), 'scrollbar-override')}
            placeholder="1024"
            value={textValue}
            onChange={handleChange}
            disabled={isReadOnly}
          />
        </div>
        <div className="col-md-6">
          <div className={classNames(css(styles.preview), 'scrollbar-override')}>
            {parsedBands.map((band, index) => (
              <div key={index}>{band}</div>
            ))}
          </div>
        </div>
      </div>
      {!isReadOnly && (
        <div className="row">
          <div className="col-md-12">
            <Button
              className={css(styles.submitButton)}
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text={isSaving ? 'Saving...' : 'Save changes'}
              onClick={async () => {
                setIsSaving(true);
                await onSave(parsedBands);
                setIsSaving(false);
              }}
              disabled={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  instructions: {
    marginBottom: '10px',
  },
  textarea: {
    height: '150px',
    textAlign: 'right',
  },
  preview: {
    overflowY: 'scroll',
    border: `1px solid ${GRAY_LIGHT}`,
    padding: '5px',
    height: '150px',
    textAlign: 'right',
  },
  submitButton: {
    float: 'right',
  },
});
